/**
 * Projektwert-Kaskade auf Projektebene (Vorgänge spielen keine Rolle, nur die Belege):
 *
 * 1. Rechnung oder Schlussrechnung vorhanden → Abgerechnet = alle gültigen Rechnungen
 *    (Abschläge + Rechnungen + Schluss − Gutschriften; stornierte Originale fallen raus,
 *    Stornorechnungen selbst zählen nicht, sonst würde doppelt gemindert).
 * 2. Angenommenes Angebot vorhanden → Auftragswert = Summe der angenommenen Angebote.
 * 3. Offenes Angebot vorhanden → Angebotssumme = Summe der offenen Angebote.
 * 4. Sonst → Kalkuliert = Summe der Kalkulation aller Vorgänge.
 *
 * Abschlagsrechnungen allein ändern die Stufe nicht. Abgelehnte Angebote zählen nie.
 * Mehrere Versionen desselben Angebots werden addiert; ist eine davon angenommen,
 * zählen in diesem Vorgang nur die angenommenen Versionen.
 * Zusätzlich gibt es eine Kontextzeile mit der Stufe darunter (wird nicht addiert).
 */

export type ProjectValueStage = 'calculated' | 'offered' | 'accepted' | 'invoiced';

export type ProjectValueContext = {
  /** 'offered' → „Angebot X“, 'accepted' → „Auftrag X“, 'partial' → „davon X abgerechnet“. */
  kind: 'offered' | 'accepted' | 'partial';
  net: number;
};

export type ProjectValue = {
  net: number;
  stage: ProjectValueStage;
  context: ProjectValueContext | null;
};

export type ProjectValueOffering = {
  id: string;
  /** Kalkulation des Vorgangs (netto), null ohne abrechenbare Position. */
  calc_net: number | null;
};

export type ProjectValueDocument = {
  id: string;
  offering_id: string | null;
  document_type: string;
  status: string | null;
  net_amount: number | null;
  issue_date: string | null;
  created_at: string | null;
};

const INACTIVE_STATUSES = new Set(['cancelled', 'draft', 'extraction_pending', 'extraction_failed', 'rejected']);

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function isActive(doc: ProjectValueDocument): boolean {
  return !INACTIVE_STATUSES.has(String(doc.status ?? '').trim());
}

function amount(doc: ProjectValueDocument): number {
  const n = Number(doc.net_amount);
  return doc.net_amount != null && Number.isFinite(n) ? n : 0;
}

export function resolveProjectValue(
  offerings: ProjectValueOffering[],
  documents: ProjectValueDocument[],
): ProjectValue | null {
  const calcByOffering = new Map(offerings.map((o) => [o.id, o.calc_net]));

  // Angebote: Versionen hängen am selben Vorgang und werden addiert.
  // Angenommen gewinnt: die übrigen Versionen dieses Vorgangs zählen dann nicht mehr.
  const offersByKey = new Map<string, ProjectValueDocument[]>();
  for (const doc of documents) {
    if (doc.document_type !== 'offer' || doc.status === 'cancelled' || doc.status === 'rejected') continue;
    const key = doc.offering_id ?? doc.id;
    offersByKey.set(key, [...(offersByKey.get(key) ?? []), doc]);
  }
  let acceptedNet = 0;
  let openNet = 0;
  let hasAccepted = false;
  let hasOpen = false;
  for (const versions of offersByKey.values()) {
    const acceptedVersions = versions.filter((d) => d.status === 'paid');
    const accepted = acceptedVersions.length > 0;
    const counted = accepted ? acceptedVersions : versions;
    // Altbelege haben oft keinen Betrag → Kalkulation des Vorgangs, je Vorgang nur einmal.
    const offeringId = counted[0].offering_id;
    const withoutAmount = counted.some((d) => d.net_amount == null);
    const value =
      counted.reduce((sum, d) => sum + amount(d), 0) +
      (withoutAmount && offeringId ? calcByOffering.get(offeringId) ?? 0 : 0);
    if (accepted) {
      hasAccepted = true;
      acceptedNet += value;
    } else {
      hasOpen = true;
      openNet += value;
    }
  }

  let billedNet = 0;
  let partialNet = 0;
  let hasInvoice = false;
  let hasPartial = false;
  for (const doc of documents) {
    if (!isActive(doc)) continue;
    if (doc.document_type === 'invoice' || doc.document_type === 'final_invoice') {
      hasInvoice = true;
      billedNet += amount(doc);
    } else if (doc.document_type === 'partial_invoice') {
      hasPartial = true;
      billedNet += amount(doc);
      partialNet += amount(doc);
    } else if (doc.document_type === 'invoice_correction') {
      billedNet -= Math.abs(amount(doc));
    }
  }

  acceptedNet = round2(acceptedNet);
  openNet = round2(openNet);
  billedNet = round2(billedNet);
  partialNet = round2(partialNet);
  const partialContext: ProjectValueContext | null = hasPartial ? { kind: 'partial', net: partialNet } : null;

  if (hasInvoice) {
    // Kontext nur, wenn das Angebot über dem Abgerechneten liegt (z. B. 300 € Begehung, Hauptangebot offen);
    // sonst ist es erledigt oder ein Altbeleg mit veralteter Kalkulation.
    const context: ProjectValueContext | null =
      hasAccepted && acceptedNet > billedNet
        ? { kind: 'accepted', net: acceptedNet }
        : hasOpen && openNet > billedNet
          ? { kind: 'offered', net: openNet }
          : null;
    return { net: billedNet, stage: 'invoiced', context };
  }
  if (hasAccepted) {
    return {
      net: acceptedNet,
      stage: 'accepted',
      context: partialContext ?? (hasOpen ? { kind: 'offered', net: openNet } : null),
    };
  }
  if (hasOpen) {
    return { net: openNet, stage: 'offered', context: partialContext };
  }

  const calcs = offerings.map((o) => o.calc_net).filter((n): n is number => n != null);
  if (hasPartial) {
    // Abschlag ohne Angebotsbeleg: wie beauftragt behandeln, mindestens die Abschläge.
    const calcNet = calcs.reduce((sum, n) => sum + n, 0);
    return { net: round2(Math.max(calcNet, partialNet)), stage: 'accepted', context: partialContext };
  }
  if (calcs.length === 0) return null;
  return { net: round2(calcs.reduce((sum, n) => sum + n, 0)), stage: 'calculated', context: null };
}

/**
 * Betrag für Liste/Kanban: Hauptwert + Angebots-/Auftragskontext (z. B. 300 € abgerechnet + 46.964 € Angebot).
 * „davon abgerechnet“ steckt schon im Hauptwert und wird nicht addiert.
 */
export function projectValueListTotal(value: ProjectValue | null | undefined): number | null {
  if (!value) return null;
  const extra = value.context && value.context.kind !== 'partial' ? value.context.net : 0;
  return round2(value.net + extra);
}

/** Betrag eines Projekts in Liste/Kanban (Fallback: net_total, z. B. vor dem Nachladen der Kanban-Summen). */
export function leadListAmount(lead: { net_total?: number | null; project_value?: ProjectValue | null }): number | null {
  return lead.project_value ? projectValueListTotal(lead.project_value) : lead.net_total ?? null;
}

const STAGE_LABEL: Record<ProjectValueStage, string> = {
  calculated: 'Kalkuliert',
  offered: 'Angebotssumme',
  accepted: 'Auftragswert',
  invoiced: 'Abgerechnet',
};

export function projectValueLabel(value: ProjectValue | null | undefined): string {
  return value ? STAGE_LABEL[value.stage] : 'Nettosumme';
}

const STAGE_SHORT_LABEL: Record<ProjectValueStage, string> = {
  calculated: 'Kalkuliert',
  offered: 'Angebot',
  accepted: 'Auftrag',
  invoiced: 'Abgerechnet',
};

/** Kurzform für schmale Flächen (Kanban-Karte, mobil). */
export function projectValueShortLabel(value: ProjectValue | null | undefined): string | null {
  return value ? STAGE_SHORT_LABEL[value.stage] : null;
}

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/** Kontextzeile unter dem Hauptwert, z. B. „Angebot 46.964,33 €“ oder „davon 5.000,00 € abgerechnet“. */
export function projectValueContextText(value: ProjectValue | null | undefined): string | null {
  const context = value?.context;
  if (!context) return null;
  if (context.kind === 'partial') return `davon ${eur.format(context.net)} abgerechnet`;
  return `${context.kind === 'accepted' ? 'Auftrag' : 'Angebot'} ${eur.format(context.net)}`;
}

/** Tooltip: Stufe (netto) und Kontextzeile. */
export function projectValueTooltip(value: ProjectValue | null | undefined): string {
  if (!value) return 'Nettosumme';
  const context = projectValueContextText(value);
  return context ? `${STAGE_LABEL[value.stage]} (netto)\n${context}` : `${STAGE_LABEL[value.stage]} (netto)`;
}
